import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Avatar,
  Chip,
  Button,
  TextField,
  Card,
  CardContent,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import {
  Close,
  Edit,
  Person,
  Note,
  Payment,
  History,
  Add,
  Delete,
  CheckCircle,
  Pending,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';

const MotionCard = motion(Card);

/**
 * Student Detail Dialog
 * Shows comprehensive student information with tabs
 * @param {number} initialTab - Tab index to open initially (0=Info, 1=Notes, 2=Payments, 3=History)
 */
function StudentDetailDialog({ open, onClose, student, onStudentUpdated, showSnackbar, initialTab = 0 }) {
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);

  // Notes state
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isImportant, setIsImportant] = useState(false);
  const [seatNo, setSeatNo] = useState('');
  const [lockerNo, setLockerNo] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Payments state
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [addingPayment, setAddingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    plan_id: '',
    amount_cents: '',
    payment_method: 'cash',
    transaction_id: '',
    notes: '',
  });

  // History state
  const [history, setHistory] = useState({ bookings: [], sessions: [] });

  useEffect(() => {
    if (open && student) {
      setCurrentTab(initialTab); // Reset to specified tab
      fetchNotes();
      fetchPayments();
      fetchPlans();
      fetchHistory();
    }
  }, [open, student]);

  const fetchNotes = async () => {
    try {
      const response = await api.get(`/librarian/students/${student.id}/notes`);
      setNotes(response.data.data.notes);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await api.get(`/librarian/students/${student.id}/payments`);
      setPayments(response.data.data.payments);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/librarian/plans');
      setPlans(response.data.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get(`/librarian/students/${student.id}/history`);
      setHistory(response.data.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() && !seatNo.trim() && !lockerNo.trim()) {
      showSnackbar('Please enter a note or seat/locker number', 'error');
      return;
    }

    try {
      await api.post(`/librarian/students/${student.id}/notes`, {
        note_text: noteText || `Assigned: ${seatNo ? `Seat ${seatNo}` : ''}${seatNo && lockerNo ? ', ' : ''}${lockerNo ? `Locker ${lockerNo}` : ''}`,
        note_type: noteType,
        is_important: isImportant,
        seat_no: seatNo || null,
        locker_no: lockerNo || null,
      });

      showSnackbar('Note added successfully', 'success');
      setNoteText('');
      setNoteType('general');
      setIsImportant(false);
      setSeatNo('');
      setLockerNo('');
      fetchNotes();
    } catch (error) {
      console.error('Error adding note:', error);
      showSnackbar('Failed to add note', 'error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await api.delete(`/librarian/notes/${noteId}`);
      showSnackbar('Note deleted successfully', 'success');
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      showSnackbar('Failed to delete note', 'error');
    }
  };

  const handlePlanSelect = (planId) => {
    const selectedPlan = plans.find(p => p.id === planId);
    if (selectedPlan) {
      setPaymentForm({
        ...paymentForm,
        plan_id: planId,
        // price_cents is actually stored in RUPEES (not paise)
        amount_cents: selectedPlan.price_cents.toString(),
      });
    } else {
      setPaymentForm({
        ...paymentForm,
        plan_id: '',
      });
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount_cents || paymentForm.amount_cents <= 0) {
      showSnackbar('Valid amount is required', 'error');
      return;
    }

    try {
      await api.post(`/librarian/students/${student.id}/payments`, {
        plan_id: paymentForm.plan_id || null,
        amount_cents: parseInt(paymentForm.amount_cents * 100), // Convert to paise
        payment_method: paymentForm.payment_method,
        transaction_id: paymentForm.transaction_id || null,
        notes: paymentForm.notes || null,
        status: 'completed',
      });

      showSnackbar('Payment recorded successfully', 'success');
      setPaymentForm({
        plan_id: '',
        amount_cents: '',
        payment_method: 'cash',
        transaction_id: '',
        notes: '',
      });
      setAddingPayment(false);
      fetchPayments();
    } catch (error) {
      console.error('Error adding payment:', error);
      showSnackbar('Failed to record payment', 'error');
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const getNoteTypeColor = (type) => {
    switch (type) {
      case 'payment':
        return 'info';
      case 'behavior':
        return 'warning';
      case 'complaint':
        return 'error';
      case 'praise':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: 'calc(100vh - 140px)',
          maxHeight: 'calc(100vh - 140px)',
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: student.blocked ? 'error.main' : 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 700,
              }}
            >
              {student.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="700">
                {student.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {student.email}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentTab}
          onChange={(e, v) => setCurrentTab(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}
        >
          <Tab icon={<Person />} label="Info" iconPosition="start" />
          <Tab icon={<Note />} label="Notes" iconPosition="start" />
          <Tab icon={<Payment />} label="Payments" iconPosition="start" />
          <Tab icon={<History />} label="History" iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Tab 0: Student Info */}
        {currentTab === 0 && (
          <Box>
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                  Personal Information
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {student.phone || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Age
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {student.age ? `${student.age} years` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      City
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {student.city || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      <Chip
                        label={student.blocked ? 'Blocked' : 'Active'}
                        size="small"
                        color={student.blocked ? 'error' : 'success'}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Interested Courses
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {student.interested_courses || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Member Since
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formatDate(student.created_at)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tab 1: Notes */}
        {currentTab === 1 && (
          <Box>
            {/* Add Note Form */}
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 2, bgcolor: 'action.hover' }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight="700" gutterBottom>
                  Add New Note
                </Typography>
                <Box display="flex" flexDirection="column" gap={2} mt={2}>
                  {/* Seat and Locker Number Fields */}
                  <Box display="flex" gap={2}>
                    <TextField
                      label="Seat No."
                      placeholder="e.g., A-12"
                      value={seatNo}
                      onChange={(e) => setSeatNo(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Locker No."
                      placeholder="e.g., L-05"
                      value={lockerNo}
                      onChange={(e) => setLockerNo(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    multiline
                    rows={3}
                    placeholder="Write a note about this student..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    fullWidth
                  />
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={noteType}
                        onChange={(e) => setNoteType(e.target.value)}
                        label="Type"
                      >
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="payment">Payment</MenuItem>
                        <MenuItem value="behavior">Behavior</MenuItem>
                        <MenuItem value="complaint">Complaint</MenuItem>
                        <MenuItem value="praise">Praise</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant={isImportant ? 'contained' : 'outlined'}
                      onClick={() => setIsImportant(!isImportant)}
                      size="small"
                    >
                      {isImportant ? 'Important' : 'Normal'}
                    </Button>
                    <Box flex={1} />
                    <Button variant="contained" onClick={handleAddNote}>
                      Add Note
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Notes List */}
            <AnimatePresence>
              {notes.length === 0 ? (
                <Alert severity="info">No notes yet for this student</Alert>
              ) : (
                notes.map((note) => (
                  <MotionCard
                    key={note.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      borderLeft: note.is_important ? 4 : 0,
                      borderLeftColor: 'error.main',
                    }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          <Chip
                            label={note.note_type}
                            size="small"
                            color={getNoteTypeColor(note.note_type)}
                          />
                          {note.is_important && (
                            <Chip label="Important" size="small" color="error" />
                          )}
                          {note.seat_no && (
                            <Chip
                              label={`Seat: ${note.seat_no}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                          {note.locker_no && (
                            <Chip
                              label={`Locker: ${note.locker_no}`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteNote(note.id)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" paragraph>
                        {note.note_text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        By {note.librarian?.name} on {formatDateTime(note.created_at)}
                      </Typography>
                    </CardContent>
                  </MotionCard>
                ))
              )}
            </AnimatePresence>
          </Box>
        )}

        {/* Tab 2: Payments */}
        {currentTab === 2 && (
          <Box>
            {/* Add Payment Button */}
            {!addingPayment ? (
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setAddingPayment(true)}
                fullWidth
                sx={{ mb: 2, borderRadius: 3 }}
              >
                Record New Payment
              </Button>
            ) : (
              <Card variant="outlined" sx={{ borderRadius: 3, mb: 2, bgcolor: 'action.hover' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="700" gutterBottom>
                    Record Payment
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2} mt={2}>
                    <FormControl fullWidth>
                      <InputLabel>Plan (Optional)</InputLabel>
                      <Select
                        value={paymentForm.plan_id}
                        onChange={(e) => handlePlanSelect(e.target.value)}
                        label="Plan (Optional)"
                      >
                        <MenuItem value="">
                          <em>No Plan / Custom Amount</em>
                        </MenuItem>
                        {plans.map((plan) => (
                          <MenuItem key={plan.id} value={plan.id}>
                            {plan.name} - ₹{plan.price_cents} ({plan.duration_days} days)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Amount (₹)"
                      type="number"
                      value={paymentForm.amount_cents}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, amount_cents: e.target.value })
                      }
                      fullWidth
                      helperText={paymentForm.plan_id ? "Auto-filled from selected plan (editable)" : "Enter custom amount"}
                    />
                    <FormControl fullWidth>
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        value={paymentForm.payment_method}
                        onChange={(e) =>
                          setPaymentForm({ ...paymentForm, payment_method: e.target.value })
                        }
                        label="Payment Method"
                      >
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="upi">UPI</MenuItem>
                        <MenuItem value="card">Card</MenuItem>
                        <MenuItem value="net_banking">Net Banking</MenuItem>
                        <MenuItem value="wallet">Wallet</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Transaction ID (optional)"
                      value={paymentForm.transaction_id}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, transaction_id: e.target.value })
                      }
                      fullWidth
                    />
                    <TextField
                      label="Notes (optional)"
                      multiline
                      rows={2}
                      value={paymentForm.notes}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, notes: e.target.value })
                      }
                      fullWidth
                    />
                    <Box display="flex" gap={1}>
                      <Button onClick={() => setAddingPayment(false)}>Cancel</Button>
                      <Button variant="contained" onClick={handleAddPayment} fullWidth>
                        Record Payment
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Payments List */}
            {payments.length === 0 ? (
              <Alert severity="info">No payment records yet for this student</Alert>
            ) : (
              payments.map((payment) => (
                <Card
                  key={payment.id}
                  variant="outlined"
                  sx={{ mb: 2, borderRadius: 3 }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" fontWeight="700" color="primary.main">
                        {formatCurrency(payment.amount_cents)}
                      </Typography>
                      <Chip
                        label={payment.status}
                        size="small"
                        color={getPaymentStatusColor(payment.status)}
                        icon={
                          payment.status === 'completed' ? (
                            <CheckCircle />
                          ) : payment.status === 'pending' ? (
                            <Pending />
                          ) : (
                            <CancelIcon />
                          )
                        }
                      />
                    </Box>
                    <Stack spacing={0.5}>
                      {payment.plan && (
                        <Typography variant="body2" color="text.secondary">
                          Plan: <strong>{payment.plan.name}</strong> ({payment.plan.duration_days} days)
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Method: <strong>{payment.payment_method}</strong>
                      </Typography>
                      {payment.transaction_id && (
                        <Typography variant="body2" color="text.secondary">
                          Transaction ID: {payment.transaction_id}
                        </Typography>
                      )}
                      {payment.notes && (
                        <Typography variant="body2" color="text.secondary">
                          {payment.notes}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Recorded on {formatDate(payment.created_at)}
                        {payment.recorder && ` by ${payment.recorder.name}`}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}

        {/* Tab 3: History */}
        {currentTab === 3 && (
          <Box>
            {/* Bookings */}
            <Typography variant="subtitle1" fontWeight="700" gutterBottom>
              Bookings
            </Typography>
            {history.bookings.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                No booking history
              </Alert>
            ) : (
              <Box mb={3}>
                {history.bookings.map((booking) => (
                  <Card
                    key={booking.id}
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 3 }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="700">
                            {booking.library?.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {booking.library?.address}
                          </Typography>
                          {booking.plan && (
                            <Typography variant="body2" color="text.secondary" mt={1}>
                              Plan: <strong>{booking.plan.name}</strong> -{' '}
                              {formatCurrency(booking.plan.price_cents)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                          </Typography>
                        </Box>
                        <Chip label={booking.status} size="small" color="primary" />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Sessions Grouped by Day */}
            <Typography variant="subtitle1" fontWeight="700" gutterBottom>
              Recent Sessions
            </Typography>
            {history.sessions.length === 0 ? (
              <Alert severity="info">No session history</Alert>
            ) : (
              history.sessions.map((dayGroup) => (
                <Card
                  key={dayGroup.date}
                  variant="outlined"
                  sx={{ mb: 3, borderRadius: 3 }}
                >
                  <CardContent>
                    {/* Day Header */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                      pb={2}
                      borderBottom={1}
                      borderColor="divider"
                    >
                      <Typography variant="subtitle1" fontWeight="700">
                        {new Date(dayGroup.date).toLocaleDateString('en-IN', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                      <Chip
                        label={`Total: ${Math.floor(dayGroup.totalDurationMinutes / 60)}h ${
                          dayGroup.totalDurationMinutes % 60
                        }m`}
                        color="primary"
                        size="small"
                      />
                    </Box>

                    {/* Sessions for this day */}
                    {dayGroup.sessions.map((session, index) => (
                      <Box
                        key={session.id}
                        sx={{
                          mb: index < dayGroup.sessions.length - 1 ? 2 : 0,
                          pb: index < dayGroup.sessions.length - 1 ? 2 : 0,
                          borderBottom: index < dayGroup.sessions.length - 1 ? 1 : 0,
                          borderColor: 'divider',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight="600">
                              {session.library?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Entry: {new Date(session.entered_at).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                            {session.exited_at && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Exit: {new Date(session.exited_at).toLocaleTimeString('en-IN', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </Typography>
                            )}
                          </Box>
                          <Box textAlign="right">
                            {session.active ? (
                              <Chip label="Active" size="small" color="success" />
                            ) : (
                              <Chip
                                label={`${Math.floor(session.durationMinutes / 60)}h ${
                                  session.durationMinutes % 60
                                }m`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default StudentDetailDialog;
